import { cartLineItemsOf } from './cartLines'
import { toPriceText, type PresentationContext } from './catalogCard'
import { toReorderQuantities } from './reorderLines'
import {
  orderDateText,
  orderReference,
  orderStatusLabel,
  orderUnitCount,
  type ApiDate,
  type OrderRowStatus,
} from './orderRows'

export interface OrderLineInput {
  sku?: string | null
  name?: string | null
  quantity?: number | null
  price?: number | null
}

export interface OrderDetailInput {
  id: string
  status?: string | null
  total?: number | null
  sequence?: number | null
  externalRefNumber?: string | null
  createdAt?: ApiDate | string | number | null
  items?: readonly OrderLineInput[] | null
  /** Present instead of `items` on a storefront whose cart is grouped. */
  groups?: readonly { items?: readonly OrderLineInput[] | null }[] | null
  checkoutRules?: readonly { ruleType?: string | null; value?: unknown }[] | null
}

export interface OrderDetailLine {
  sku: string
  name: string
  quantity: number
  amountText: string
}

export interface OrderDetailModel {
  id: string
  reference: string
  status: OrderRowStatus
  dateText: string
  units: number
  totalText: string
  lines: OrderDetailLine[]
  /** The stored selections, as raw tokens — an ISO date and `CASH`/`CREDIT`. */
  deliveryToken: string | null
  paymentToken: string | null
  /** sku -> quantity, which is what refilling a cart needs. */
  reorder: Record<string, number>
}

/**
 * A stored checkout selection is `{data, amount}` under a `ruleType`, the same
 * option object the shopper picked. Only `data` identifies it, and only
 * `ruleType` says which rule it belongs to — `type` is always null and is
 * deprecated, so reading it finds nothing.
 */
export const checkoutRuleToken = (
  rules: OrderDetailInput['checkoutRules'],
  ruleType: string,
): string | null => {
  const found = (rules ?? []).find((rule) => rule?.ruleType === ruleType)
  const value = found?.value
  if (value === null || value === undefined) return null
  if (typeof value === 'string') return value
  const data = (value as { data?: unknown }).data
  return typeof data === 'string' ? data : null
}

export const toOrderDetailModel = (
  order: OrderDetailInput,
  presentation: PresentationContext,
  locale: string,
): OrderDetailModel => {
  const items = cartLineItemsOf<OrderLineInput>(order)

  const lines: OrderDetailLine[] = items
    .filter((item) => Boolean(item?.sku))
    .map((item) => {
      const quantity = item.quantity ?? 0
      return {
        sku: item.sku!,
        name: item.name ?? item.sku!,
        quantity,
        // The line total, not the unit price: the row says "×3" beside it, and
        // showing the unit price next to a quantity reads as the line's cost.
        amountText: toPriceText((item.price ?? 0) * quantity, presentation),
      }
    })

  return {
    id: order.id,
    reference: orderReference(order),
    status: orderStatusLabel(order.status),
    dateText: orderDateText(order.createdAt, locale),
    units: orderUnitCount(items),
    totalText: toPriceText(order.total ?? 0, presentation),
    lines,
    deliveryToken: checkoutRuleToken(order.checkoutRules, 'DELIVERY_DATES'),
    paymentToken: checkoutRuleToken(order.checkoutRules, 'PAYMENT_METHODS'),
    // Same sku on two lines — units and a case — is one reorder entry, which
    // is `toReorderQuantities`' whole job; it is shared with the last order so
    // both ways into "repeat this" fold duplicates the same way.
    reorder: toReorderQuantities(lines),
  }
}
